import React, { useEffect, useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { api } from "@/lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// Aliases: ip-api country name -> world-atlas properties.name
const NAME_ALIASES = {
  "United States": "United States of America",
  "Tanzania": "United Republic of Tanzania",
  "Democratic Republic of the Congo": "Dem. Rep. Congo",
  "Congo": "Congo",
  "Czechia": "Czech Republic",
  "Dominican Republic": "Dominican Rep.",
  "Bosnia and Herzegovina": "Bosnia and Herz.",
  "South Korea": "South Korea",
  "North Korea": "North Korea",
};

const normalize = (n) => (n || "").trim().toLowerCase();

// Interpolate light violet (few) -> deep violet (many). Darker = more.
const LOW = [167, 139, 250]; // #A78BFA
const HIGH = [76, 29, 149]; // #4C1D95
const colorFor = (count, max) => {
  if (!count) return "#241B38";
  const t = max <= 1 ? 1 : (count - 1) / (max - 1);
  const c = LOW.map((lo, i) => Math.round(lo + (HIGH[i] - lo) * t));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
};

export const AdminStats = ({ password }) => {
  const [stats, setStats] = useState(null);
  const [tooltip, setTooltip] = useState(null); // { name, count, x, y }
  const [selected, setSelected] = useState(null); // mobile click { name, count }

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/admin/stats`, { params: { password } });
        setStats(data);
      } catch (e) {}
    })();
  }, [password]);

  const { countMap, maxCount } = useMemo(() => {
    const map = {};
    let max = 0;
    (stats?.countries || []).forEach((c) => {
      const key = normalize(NAME_ALIASES[c.country] || c.country);
      map[key] = { count: c.count, label: c.country };
      if (c.count > max) max = c.count;
    });
    return { countMap: map, maxCount: max };
  }, [stats]);

  const lookup = (geoName) => {
    const direct = countMap[normalize(geoName)];
    if (direct) return direct;
    return null;
  };

  if (!stats) return <p className="muted text-sm mt-6" data-testid="admin-stats-loading">Chargement des statistiques…</p>;

  const s = stats.signups;

  return (
    <div data-testid="admin-stats-panel">
      <div className="admin-stats stats-signups">
        <div className="admin-stat"><p className="eyebrow">AUJOURD'HUI</p><p className="admin-stat-value" data-testid="stat-today">{s.today}</p></div>
        <div className="admin-stat"><p className="eyebrow">7 JOURS</p><p className="admin-stat-value" data-testid="stat-7">{s.last7}</p></div>
        <div className="admin-stat"><p className="eyebrow">30 JOURS</p><p className="admin-stat-value" data-testid="stat-30">{s.last30}</p></div>
        <div className="admin-stat"><p className="eyebrow">TOTAL</p><p className="admin-stat-value accent" data-testid="stat-total">{s.total}</p></div>
      </div>

      <div className="admin-heading mt-12"><p className="eyebrow">CARTE DES INSCRIPTIONS</p><h2>D'où viennent tes <em>utilisateurs.</em></h2></div>

      <div className="stats-map-wrap" data-testid="stats-map">
        <ComposableMap
          projectionConfig={{ scale: 145 }}
          width={900}
          height={440}
          style={{ width: "100%", height: "auto" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const hit = lookup(name);
                const count = hit ? hit.count : 0;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={colorFor(count, maxCount)}
                    stroke="#0E0A1A"
                    strokeWidth={0.4}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", fill: count ? "#8B5CF6" : "#3A2E55", cursor: "pointer" },
                      pressed: { outline: "none" },
                    }}
                    onMouseEnter={(e) => setTooltip({ name, count, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setTooltip((t) => (t ? { ...t, x: e.clientX, y: e.clientY } : null))}
                    onMouseLeave={() => setTooltip(null)}
                    onClick={() => setSelected({ name, count })}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {tooltip && (
          <div className="stats-map-tooltip" style={{ left: tooltip.x + 12, top: tooltip.y + 12 }} data-testid="stats-map-tooltip">
            <strong>{tooltip.name}</strong>
            <span>{tooltip.count} inscrit{tooltip.count > 1 ? "s" : ""}</span>
          </div>
        )}
      </div>

      {selected && (
        <p className="stats-map-selected" data-testid="stats-map-selected">
          {selected.name} — {selected.count} inscrit{selected.count > 1 ? "s" : ""}
        </p>
      )}

      <div className="admin-heading mt-10"><p className="eyebrow">TOP 5 PAYS</p></div>
      <div className="stats-ranking" data-testid="stats-ranking">
        {stats.top5.length === 0 && <p className="muted text-sm" data-testid="stats-ranking-empty">Aucune donnée de pays pour le moment.</p>}
        {stats.top5.map((c, i) => (
          <div key={c.country} className="stats-rank-row" data-testid={`stats-rank-${i}`}>
            <span className="stats-rank-pos">{i + 1}</span>
            <span className="stats-rank-name">{c.country}</span>
            <span className="stats-rank-bar-wrap"><span className="stats-rank-bar" style={{ width: `${maxCount ? (c.count / maxCount) * 100 : 0}%` }} /></span>
            <span className="stats-rank-count">{c.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
